import { apiErrorText } from "../../../api/api-error.js";
import { portalText } from "../../../i18n/portal.js";
/**
 * The information inputs in the wizard — plan-018 WAPP-A (design §30.12, §30.6.1, §30.4.2; ADR 0059, ED-31).
 *
 * A template's field set may now carry a `website` field (one public website address), an `upload` that takes several
 * files (`maxFileCount`) and fields that share a `requiredGroup` (at least one of them must be given). Everything here
 * is read from the field set — never from a template's code (F-FAC-1: a grep for the template's code finds nothing in this
 * directory):
 *
 * ```
 *   the website address     typed text → websiteAddressTextOf (https:// added when no scheme is written) →
 *                           websiteAddressRefusalOf (the api's own rule, no DNS) → one sentence per reason, en and el;
 *                           sent in its stored form (storedWebsiteAddressOf), left out when empty
 *   a required group        none of its fields given → one sentence naming what each field asks, by kind:
 *                           "Add your information: paste text, attach a document or give your website address."
 *   the api's refusals      website_address_refused, information_input_missing (field "field_value.<code>") and
 *                           information_input_too_large (413, at upload) → the same sentences, in the portal's
 *                           language, placed under the control they belong to (informationRefusalOf)
 * ```
 *
 * The `error.detail` the api sends (`reason`, `group`, `field_code[]`) does not reach the portal — the generated client
 * keeps code, message and field only — so the reason and the group are read again from the answer and the field set
 * the request was made from. The rule is the api's own function, so the two cannot disagree on a reason.
 */
import { ApiError } from "@wetalk/api-client";
import {
  PASTED_TEXT_BYTE_LIMIT,
  WEBSITE_PAGE_LIMIT,
  storedWebsiteAddressOf,
  websiteAddressRefusalOf,
  type AgentTemplateField,
  type WebsiteAddressRefusalReason,
} from "@wetalk/domain";

// ── the website address ────────────────────────────────────────────────────────────────────

/** A scheme the person wrote ("https:", "ftp:", "mailto:"), as opposed to a host followed by a port ("example.gr:8080"). */
const WRITTEN_SCHEME = /^[a-z][a-z0-9+.-]*:(?!\d)/i;

/**
 * The text the website rule reads: what was typed, trimmed, with `https://` in front when no scheme was written — an
 * owner types "roma-pizzeria.gr" far more often than the full address. A written scheme is kept, so "ftp://…" is still
 * refused for its scheme.
 */
export function websiteAddressTextOf(typed: string): string {
  const trimmed = typed.trim();
  if (trimmed === "" || trimmed.includes("://") || WRITTEN_SCHEME.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** The sentence for each refusal reason, in the portal's language (the api says the same in English). */
export function websiteRefusalTextOf(reason: WebsiteAddressRefusalReason): string {
  switch (reason) {
    case "malformed":
      return portalText("core", "wizard.information.website_malformed");
    case "scheme":
      return portalText("core", "wizard.information.website_scheme");
    case "port":
      return portalText("core", "wizard.information.website_port");
    case "credentials":
      return portalText("core", "wizard.information.website_user_name");
    case "ip_literal":
      return portalText("core", "wizard.information.website_ip_literal");
    case "not_public_host":
      return portalText("core", "wizard.information.website_not_public");
    case "too_long":
      return portalText("core", "wizard.information.website_too_long");
  }
}

/** Why a typed address cannot be used, in words — or `null` when it can (an empty one is the group's question). */
export function websiteProblemOf(typed: string): string | null {
  const text = websiteAddressTextOf(typed);
  if (text === "") return null;
  const refusal = websiteAddressRefusalOf(text);
  return refusal === null ? null : websiteRefusalTextOf(refusal.reason);
}

/** The value sent as `field_value.<code>`: the stored form, or `null` while the box is empty or refused. */
export function websiteAddressSentOf(typed: string): string | null {
  const text = websiteAddressTextOf(typed);
  if (text === "" || websiteAddressRefusalOf(text) !== null) return null;
  return storedWebsiteAddressOf(text);
}

/** The line under the website box: what is read, and what is not. */
export function websiteDescriptionText(): string {
  return portalText("core", "wizard.information.website_description", { v0: String(WEBSITE_PAGE_LIMIT) });
}

export function websitePlaceholderText(): string {
  return portalText("core", "wizard.information.website_placeholder");
}

// ── required groups ────────────────────────────────────────────────────────────────────────

/** The key a required group's problem is filed under, beside the field codes. */
export function requiredGroupKeyOf(group: string): string {
  return `required_group:${group}`;
}

/** What giving one field of a group means, in a few words — by its kind, never by its code. */
function givingTextOf(field: AgentTemplateField): string {
  if (field.kind === "upload") {
    return field.allowText === true
      ? portalText("core", "wizard.information.give_typed_text")
      : portalText("core", "wizard.information.give_document");
  }
  if (field.kind === "website") return portalText("core", "wizard.information.give_website");
  return portalText("core", "wizard.information.give_answer", { v0: field.label });
}

/** "a, b or c" in the portal's language. */
function eitherOf(word: readonly string[]): string {
  if (word.length <= 1) return word.join("");
  return portalText("core", "wizard.information.either", { v0: word.slice(0, -1).join(", "), v1: String(word.at(-1)) });
}

/** "Add your information: paste text, attach a document or give your website address." */
export function requiredGroupTextOf(member: readonly AgentTemplateField[]): string {
  return portalText("core", "wizard.information.group_missing", { v0: eitherOf(member.map(givingTextOf)) });
}

// ── an upload that may be typed (`allowText`) ──────────────────────────────────────────────

export interface TypedUploadCopy {
  /** The box's example text. */
  readonly placeholder: string;
  /** The button that swaps the dropzone for the box. */
  readonly typeInstead: string;
  /** The sentence while nothing is given and the field must be. */
  readonly missing: string;
  /** How tall the box is. */
  readonly row: number;
  /**
   * Live bug 2026-10-28: the sentence beside the box while the field holds another document (kept by Edit, or picked) —
   * what is typed replaces that file entirely, so everything it held must be in the text.
   */
  readonly replaces: (file: string) => string;
}

/**
 * The copy of a typed upload, by FIELD CODE — a register of what each typed field holds, like `field-format.ts`'s
 * formats, not a branch per template. A field it does not name keeps the questionnaire's copy, as before.
 */
export function typedUploadCopyOf(field: Extract<AgentTemplateField, { kind: "upload" }>): TypedUploadCopy {
  if (field.code === "information_text") {
    return {
      placeholder: portalText("core", "wizard.information.typed_placeholder"),
      typeInstead: portalText("core", "wizard.information.type_instead"),
      missing: portalText("core", "wizard.information.typed_missing"),
      row: 8,
      replaces: (file) => portalText("operations", "wizard.typed_replace.information_notice", { file }),
    };
  }
  return {
    placeholder: portalText("operations", "one_question_per_line_e_g_how_was_your_last_order_from_one_b034b5f0"),
    typeInstead: portalText("operations", "or_type_them_here_001f19a3"),
    missing: portalText("operations", "paste_the_questions_or_attach_a_file_for_this_arm_f4030a50"),
    row: 5,
    replaces: (file) => portalText("operations", "wizard.typed_replace.questions_notice", { file }),
  };
}

/** The sentence for typed or attached text over the api's limit (`413 information_input_too_large`). */
export function typedTooLargeText(): string {
  return portalText("core", "wizard.information.typed_too_large", { v0: String(PASTED_TEXT_BYTE_LIMIT / 1024) });
}

/** Whether a typed upload's file is over the limit the api refuses at upload. */
export function isOverTypedLimit(field: Extract<AgentTemplateField, { kind: "upload" }>, file: File | undefined): boolean {
  return field.allowText === true && file !== undefined && file.size > PASTED_TEXT_BYTE_LIMIT;
}

// ── several files in one upload field (`maxFileCount`) ─────────────────────────────────────

/** "Up to 10 files: TXT, PDF or PNG." */
export function fileLimitHintText(most: number, accept: readonly string[]): string {
  const word = accept.map((one) => one.replace(/^\./, "").toUpperCase());
  const kind = word.length <= 1 ? word.join("") : portalText("operations", "value_or_value_803267df", { v0: word.slice(0, -1).join(", "), v1: String(word.at(-1)) }).replace(/\.$/, "");
  return portalText("core", "wizard.information.file_limit_hint", { v0: String(most), v1: kind });
}

export function fileLimitReachedText(most: number): string {
  return portalText("core", "wizard.information.file_limit_reached", { v0: String(most) });
}

export function filesLeftOutText(most: number, leftOut: readonly string[]): string {
  return portalText("core", "wizard.information.files_left_out", { v0: String(most), v1: leftOut.join(", ") });
}

export function fileUnreadableText(name: readonly string[]): string {
  return portalText("core", "wizard.information.file_unreadable", { v0: name.join(", ") });
}

export function dropFilesText(): string {
  return portalText("core", "wizard.information.drop_files");
}

export function removeFileText(name: string): string {
  return portalText("core", "wizard.information.remove_file", { v0: name });
}

export function removeText(): string {
  return portalText("core", "wizard.information.remove");
}

// ── the api's refusals, placed and worded ──────────────────────────────────────────────────

/** A refusal the wizard can place: under which control (a field code or a group key), and in which words. */
export interface InformationRefusal {
  readonly key: string;
  readonly text: string;
}

function fieldCodeOf(error: ApiError): string | null {
  const field = error.field;
  return field !== null && field.startsWith("field_value.") ? field.slice("field_value.".length) : null;
}

/**
 * The api's information refusal, in the portal's language and placed under its control — or `null` for any other
 * error (the screen then shows {@link apiErrorText} as before). A refusal the wizard's own rule does not reproduce
 * (the two disagreeing) keeps the api's sentence.
 */
export function informationRefusalOf(
  error: unknown,
  fieldSet: readonly AgentTemplateField[],
  fieldValue: Readonly<Record<string, string>>,
): InformationRefusal | null {
  if (!(error instanceof ApiError)) return null;
  if (error.code === "website_address_refused") {
    const code = fieldCodeOf(error) ?? fieldSet.find((one) => one.kind === "website")?.code;
    if (code === undefined) return null;
    const problem = websiteProblemOf(fieldValue[code] ?? "");
    return { key: code, text: problem ?? apiErrorText(error, "") };
  }
  if (error.code === "information_input_missing") {
    const code = fieldCodeOf(error);
    const group = fieldSet.find((one) => one.code === code)?.requiredGroup;
    if (group === undefined) return null;
    return { key: requiredGroupKeyOf(group), text: requiredGroupTextOf(fieldSet.filter((one) => one.requiredGroup === group)) };
  }
  if (error.code === "information_input_too_large") {
    const typed = fieldSet.find((one) => one.kind === "upload" && one.allowText === true);
    if (typed === undefined) return null;
    return { key: typed.code, text: typedTooLargeText() };
  }
  return null;
}

/** The sentence for a failed upload or build: the information refusals in the portal's words, the rest as before. */
export function wizardErrorText(error: unknown, fieldSet: readonly AgentTemplateField[], fieldValue: Readonly<Record<string, string>>): string {
  return informationRefusalOf(error, fieldSet, fieldValue)?.text ?? apiErrorText(error, "");
}
